import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import StatementCard from "@/components/feature/StatementCard";
import { useSeo } from "@/hooks/useSeo";
import { categories } from "@/mocks/categories";
import { statements } from "@/mocks/statements";
import type { Statement } from "@/types/content";

const tabs = [{ id: "all", name: "全部" }, ...categories];

export default function StatementsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") ?? "all";
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const [sort, setSort] = useState<"desc" | "asc">("desc");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return [...statements]
      .filter((item) => (activeCategory === "all" ? true : item.categoryId === activeCategory))
      .filter((item) => {
        if (!kw) return true;
        const haystack = `${item.title} ${item.summary} ${item.verdict} ${item.tags.join(" ")} ${item.sections
          .map((s) => `${s.heading} ${s.paragraphs.join(" ")}`)
          .join(" ")}`.toLowerCase();
        return haystack.includes(kw);
      })
      .sort((a, b) => (sort === "desc" ? (a.date < b.date ? 1 : -1) : a.date < b.date ? -1 : 1));
  }, [activeCategory, keyword, sort]);

  const selectCategory = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id === "all") next.delete("category");
    else next.set("category", id);
    setSearchParams(next, { replace: true });
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set("q", keyword.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const currentName =
    activeCategory === "all" ? "全部公告" : categories.find((c) => c.id === activeCategory)?.name ?? "全部公告";

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "声明公告库",
        description: "黑料官方声明、辟谣澄清、官方回应与公告通知的完整归档列表。",
        inLanguage: "zh-CN",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: statements.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.title,
            url: `/statements/${item.id}`,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "声明公告库", item: "/statements" },
        ],
      },
    ],
    [],
  );

  useSeo({
    title: "声明公告库 - 黑料官方声明辟谣澄清全文归档",
    description:
      "黑料官方声明公告库按官方声明、辟谣澄清、官方回应、公告通知四类归档，支持关键词搜索与时间排序，帮助快速定位公告原文与核实结论。",
    keywords: "黑料官方公告库,官方声明全文,辟谣澄清列表,官方回应,公告通知",
    path: "/statements",
    jsonLd,
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHeader
          eyebrow="全文归档 · 来源可溯"
          title="声明公告库"
          description="所有正式表态按发布时间与类别整理，保留原文与归档信息。可用关键词定位事件，或按类别逐条翻阅。"
          crumbs={[{ label: "首页", to: "/" }, { label: "声明公告库" }]}
        />

        <section className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2 rounded-full border border-background-200 bg-background-50 p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => selectCategory(tab.id)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors cursor-pointer ${
                    activeCategory === tab.id
                      ? "bg-primary-500 font-medium text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>

            <form onSubmit={submitSearch} className="flex w-full items-center gap-2 lg:w-auto">
              <div className="relative flex-1 lg:w-72">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line" />
                </span>
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索事件关键词，如 信息泄露"
                  aria-label="搜索公告关键词"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                搜索
              </button>
            </form>
          </div>

          <div className="mt-6 flex flex-col gap-3 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-600">
              当前分类：<span className="font-medium text-foreground-900">{currentName}</span>
              <span className="ml-2 text-foreground-400">共 {filtered.length} 篇</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSort("desc")}
                className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors cursor-pointer ${
                  sort === "desc"
                    ? "border-primary-300 bg-primary-50 text-primary-700"
                    : "border-background-300 text-foreground-600 hover:border-primary-300"
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center"><i className="ri-sort-desc" /></span>
                最新优先
              </button>
              <button
                type="button"
                onClick={() => setSort("asc")}
                className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs transition-colors cursor-pointer ${
                  sort === "asc"
                    ? "border-primary-300 bg-primary-50 text-primary-700"
                    : "border-background-300 text-foreground-600 hover:border-primary-300"
                }`}
              >
                <span className="flex h-4 w-4 items-center justify-center"><i className="ri-sort-asc" /></span>
                最早优先
              </button>
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item: Statement) => (
                <StatementCard key={item.id} statement={item} />
              ))}
            </div>
          ) : (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                <i className="ri-search-eye-line" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到匹配的公告</h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                试试更换关键词，或切换到「全部」分类查看已归档的所有正式表态。若仍无结果，可通过联系我们提交线索。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams(new URLSearchParams(), { replace: true });
                }}
                className="mt-6 inline-flex whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 cursor-pointer"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}